import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { usePostMilestone } from '../../api/milestone-api/milestone-api';
import { usePostPhase } from '../../api/phase-api/phase-api';
import { MilestoneDto, MilestoneType, PhaseDto, PhaseLifecycle, ReleaseDto } from '../../api/model';
import { useToast } from '../ui/Toast/ToastProvider';

import { usePostReleaseCreate } from '../../hooks/usePostReleaseCreate';
import { getPhaseLabel } from '../../utils/phaseUtils';
import { usePostSprintCreate } from '../../hooks/usePostSprintCreate';
import { getApiErrorMessage } from '../../hooks/useApiMutation';

import styles from './AddNewItemPopup.module.scss';
import TextLimitHint from '../../components/ui/TextLimitHint/TextLimitHint';
import { LONG_DESCRIPTION, SHORT_DESCRIPTION } from '../../utils/textLimits';

interface AddNewItemPopupProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
  projectType?: string;
  methodType?: string;
  onPhaseCreated?: (phaseData: PhaseDto) => void;
  onMilestoneCreated?: () => void;
  onReleaseCreated?: () => void;
  onSprintCreated?: () => void;
  phases?: PhaseDto[];
  releases?: ReleaseDto[];
}

const formConfigs: Record<string, any> = {
  Phase: {
    title: 'Add New Phase',
    description: 'Create a new project phase with timeline details.',
    submitButtonText: 'Add Phase',
    validation: (data: any) => {
      if (!data.type || !data.startTime || !data.endTime || !data.projectId) {
        return { 
          isValid: false, 
          message: 'PHASE_REQ' 
        };
      }
      if (data.subTypeAgile && data.subTypeAgile.length > 50) {
        return {
          isValid: false,
          message: 'SUBTYPE_MAX'
        };
      }
      // Validate dates
      if (new Date(data.endTime) < new Date(data.startTime)) {
        return {
          isValid: false,
          message: 'END_BEFORE_START'
        };
      }
      return { isValid: true };
    }
  },
  Releases: {
    title: 'Add New Release',
    description: 'Create a new release milestone for your project.',
    submitButtonText: 'Add Release',
    validation: (data: any) => {
      if (!data.name || !data.startDate || !data.endDate || !data.releaseDate || !data.projectId) {
        return { 
          isValid: false, 
          message: 'RELEASE_REQ' 
        };
      }
      if (data.name.length > 60) {
        return {
          isValid: false,
          message: 'RELEASE_NAME_MAX'
        };
      }
      // Validate dates
      if (new Date(data.endDate) < new Date(data.startDate)) {
        return {
          isValid: false,
          message: 'END_BEFORE_START'
        };
      }
      return { isValid: true };
    }
  },
  Sprints: {
    title: 'Add New Sprint',
    description: 'Create a new sprint for your project.',
    submitButtonText: 'Add Sprint',
    validation: (data: any) => {
      if (!data.name || !data.startDate || !data.endDate || !data.phase) {
        return { 
          isValid: false, 
          message: 'SPRINT_REQ' 
        };
      }
      if (data.name.length > 50) {
        return {
          isValid: false,
          message: 'SPRINT_NAME_MAX'
        };
      }

      // Validate end dates are not before start dates
      if (new Date(data.endDate) < new Date(data.startDate)) {
        return {
          isValid: false,
          message: 'END_BEFORE_START'
        };
      }
      
      /* Temporarily disabled based on team feedback
      // If related to release is checked, validate that a release is selected
      if (data.isRelatedToRelease && !data.releaseId) {
        return {
          isValid: false,
          message: 'Please select a release for this sprint.'
        };
      }
      */
      
      return { isValid: true };
    }
  },
  Milestones: {
    title: 'Add New Milestone',
    description: 'Create a new milestone for your project timeline.',
    submitButtonText: 'Add Milestone',
    validation: (data: any) => {
      if (!data.name || !data.milestoneType || !data.deadline) {
        return {
          isValid: false,
          message: 'MILESTONE_REQ'
        };
      }
      if (data.name.length > 80) {
        return {
          isValid: false,
          message: 'MILESTONE_NAME_MAX'
        };
      }
      return { isValid: true };
    }
  }
};

// Form components
const PhaseForm: React.FC<{ formData: any; onInputChange: (field: string, value: any) => void }> = ({ formData, onInputChange }) => {
  const { t } = useTranslation('gantt');
  // Check if project method is AGILE to conditionally render the subTypeAgile field
  const isAgileProject = formData.methodType === 'AGILE';
  
  return (
    <>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.phaseType')}</label>
        <div className={styles.selectField}>
          <select
            className={styles.select}
            value={formData.type}
            onChange={(e) => onInputChange('type', e.target.value)}
          >
            <option value="">{t('addNew.selects.phaseType')}</option>
            <option value="INITIALIZATION">{t('phaseTypes.INITIALIZATION')}</option>
            <option value="PLANNING_AND_DESIGN">{t('phaseTypes.PLANNING_AND_DESIGN')}</option>
            <option value="DEVELOPMENT">{t('phaseTypes.DEVELOPMENT')}</option>
            <option value="TESTING">{t('phaseTypes.TESTING')}</option>
            <option value="ACCEPTANCE">{t('phaseTypes.ACCEPTANCE')}</option>
            <option value="HANDOVER">{t('phaseTypes.HANDOVER')}</option>
          </select>
          <IconChevronDown />
        </div>
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.startDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.startTime}
          onChange={(e) => onInputChange('startTime', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.endDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.endTime}
          onChange={(e) => onInputChange('endTime', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>{t('addNew.labels.phaseDescription')}</label>
        <textarea
          className={styles.input}
          value={formData.phaseDescription}
          onChange={(e) => onInputChange('phaseDescription', e.target.value)}
          rows={3}
          maxLength={SHORT_DESCRIPTION}
          placeholder={t('addNew.placeholders.phaseDescription')}
        />
        <TextLimitHint value={formData.phaseDescription} max={SHORT_DESCRIPTION} />
      </div>
      {isAgileProject && (
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>{t('addNew.labels.subTypeAgile')}</label>
          <input
            type="text"
            className={styles.input}
            value={formData.subTypeAgile || ''}
            onChange={(e) => onInputChange('subTypeAgile', e.target.value)}
            maxLength={50}
            placeholder={t('addNew.placeholders.subTypeAgile')}
          />
        </div>
      )}
    </>
  );
};

const ReleasesForm: React.FC<{ formData: any; onInputChange: (field: string, value: any) => void }> = ({ formData, onInputChange }) => {
  const { t } = useTranslation('gantt');
  return (
    <>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.releaseName')}</label>
        <input
          type="text"
          className={styles.input}
          value={formData.name}
          onChange={(e) => onInputChange('name', e.target.value)}
          maxLength={60}
          placeholder={t('addNew.placeholders.releaseName')}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.startDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.startDate}
          onChange={(e) => onInputChange('startDate', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.endDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.endDate}
          onChange={(e) => onInputChange('endDate', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.releaseDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.releaseDate}
          onChange={(e) => onInputChange('releaseDate', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>{t('addNew.labels.codeFreezeOptional')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.releaseCodeFreeze}
          onChange={(e) => onInputChange('releaseCodeFreeze', e.target.value)}
        />
      </div>
    </>
  );
};

const SprintsForm: React.FC<{ formData: any; onInputChange: (field: string, value: any) => void }> = ({ formData, onInputChange }) => {
  const { t } = useTranslation('gantt');
  return (
    <>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.sprintName')}</label>
        <input
          type="text"
          className={styles.input}
          value={formData.name}
          onChange={(e) => onInputChange('name', e.target.value)}
          maxLength={50}
          placeholder={t('addNew.placeholders.sprintName')}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.startDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.startDate}
          onChange={(e) => onInputChange('startDate', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.endDate')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.endDate}
          onChange={(e) => onInputChange('endDate', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.associatedPhase')}</label>
        <div className={styles.selectField}>
          <select
            className={styles.select}
            value={formData.phase}
            onChange={(e) => onInputChange('phase', e.target.value)}
          >
            <option value="">{t('addNew.selects.phase')}</option>
            {formData.phases?.map((phase: any) => (
                <option key={phase.id} value={phase.id}>
                    {getPhaseLabel(phase, formData.phases ?? [])}
                </option>
            ))}
          </select>
          <IconChevronDown />
        </div>
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>{t('addNew.labels.codeFreeze')}</label>
        <div className={styles.checkboxField}>
          <input
            type="checkbox"
            id="codeFreeze"
            className={styles.checkbox}
            checked={!!formData.sprintCodeFreeze}
            onChange={(e) => onInputChange('sprintCodeFreeze', e.target.checked)}
          />
          <label htmlFor="codeFreeze">{t('addNew.labels.enableCodeFreeze')}</label>
        </div>
      </div>
    </>
  );
};

const MilestonesForm: React.FC<{
  formData: any;
  onInputChange: (field: string, value: any) => void;
}> = ({ formData, onInputChange }) => {
  const { t } = useTranslation('gantt');
  return (
    <>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.milestoneName')}</label>
        <input
          type="text"
          className={styles.input}
          value={formData.name}
          onChange={(e) => onInputChange('name', e.target.value)}
          maxLength={80}
          placeholder={t('addNew.placeholders.milestoneName')}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>{t('addNew.labels.milestoneDescription')}</label>
        <textarea
          className={styles.input}
          value={formData.milestoneDescription}
          onChange={(e) => onInputChange('milestoneDescription', e.target.value)}
          rows={3}
          maxLength={LONG_DESCRIPTION}
          placeholder={t('addNew.placeholders.milestoneDescription')}
        />
        <TextLimitHint value={formData.milestoneDescription} max={LONG_DESCRIPTION} />
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.milestoneType')}</label>
        <div className={styles.selectField}>
          <select
            className={styles.select}
            value={formData.milestoneType}
            onChange={(e) => onInputChange('milestoneType', e.target.value)}
          >
            <option value="">{t('addNew.selects.milestoneType')}</option>
            <option value={MilestoneType.START}>{t('milestoneTypes.START')}</option>
            <option value={MilestoneType.DECISION}>{t('milestoneTypes.DECISION')}</option>
            <option value={MilestoneType.APPROVAL}>{t('milestoneTypes.APPROVAL')}</option>
            <option value={MilestoneType.AVAILABILITY}>{t('milestoneTypes.AVAILABILITY')}</option>
            <option value={MilestoneType.ACCEPTANCE}>{t('milestoneTypes.ACCEPTANCE')}</option>
            <option value={MilestoneType.COMPLETION}>{t('milestoneTypes.COMPLETION')}</option>
            <option value={MilestoneType.GATE_CHECKPOINT}>{t('milestoneTypes.GATE_CHECKPOINT')}</option>
            <option value={MilestoneType.GO_LIVE}>{t('milestoneTypes.GO_LIVE')}</option>
            <option value={MilestoneType.EXTERNAL_DEADLINE}>{t('milestoneTypes.EXTERNAL_DEADLINE')}</option>
          </select>
          <IconChevronDown />
        </div>
      </div>
      <div className={styles.inputGroup}>
        <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.labels.deadline')}</label>
        <input
          type="date"
          className={styles.input}
          value={formData.deadline}
          onChange={(e) => onInputChange('deadline', e.target.value)}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>{t('addNew.labels.releaseRelevant')}</label>
        <div className={styles.checkboxField}>
          <input
            type="checkbox"
            id="releaseRelevant"
            className={styles.checkbox}
            checked={!!formData.releaseRelevant}
            onChange={(e) => onInputChange('releaseRelevant', e.target.checked)}
          />
          <label htmlFor="releaseRelevant">{t('addNew.labels.milestoneReleaseRelevant')}</label>
        </div>
      </div>
    </>
  );
};

const AddNewItemPopup: React.FC<AddNewItemPopupProps> = ({ 
  isOpen, 
  onClose, 
  projectId, 
  projectType, 
  methodType, 
  onPhaseCreated, 
  onMilestoneCreated,
  onReleaseCreated,
  onSprintCreated,
  phases = [],
  releases = []
}) => {
  const { t } = useTranslation('gantt');
  const firstInputRef = useRef<HTMLSelectElement>(null);
  
  // Get available form types
  const formTypes = (methodType === 'AGILE' || methodType === 'HYBRID') 
    ? ['Phase', 'Releases', 'Sprints', 'Milestones']
    : ['Phase', 'Releases', 'Milestones'];
  
  const [selectedType, setSelectedType] = useState(formTypes[0]);
  const [formData, setFormData] = useState({
    // Common fields
    name: '',
    
    // Phase form fields
    type: '',
    startTime: '',
    endTime: '',
    subTypeAgile: '',
    
    // Release fields
    startDate: '',
    endDate: '',
    releaseDate: '',
    releaseCodeFreeze: '',
    
    // Sprint fields
    sprintCodeFreeze: false,
    phase: '',
    phases: phases,
    isRelatedToRelease: false,
    releaseId: '',
    releases: releases,
    
    phaseDescription: '',

    
    // Milestone fields
    milestoneType: '',
    milestoneDescription: '',
    deadline: '',
    releaseRelevant: true,
    
    projectId: projectId === undefined ? '' : String(projectId),
    projectType: projectType || '',
    methodType: methodType || ''
  });

  const createMilestoneMutation = usePostMilestone();
  const createPhaseMutation = usePostPhase();
  const { createRelease } = usePostReleaseCreate();
  const { createSprint } = usePostSprintCreate();
  const toast = useToast();

  useEffect(() => {
    // Update phases in the form data when they change
    setFormData(prev => ({
      ...prev,
      phases: phases,
      releases: releases
    }));
  }, [phases, releases]);
  useEffect(() => {
    if (!formTypes.includes(selectedType)) {
      setSelectedType(formTypes[0]);
    }
  }, [methodType, selectedType, formTypes]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; 
      
      // Focus the first input
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field === 'releaseId' && typeof value === 'string' && value) {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async () => {
    const validationResult = formConfigs[selectedType].validation(formData);
    if (!validationResult.isValid) {
      const msgMap: Record<string,string> = {
        PHASE_REQ: 'addNew.validation.phaseRequired',
        SUBTYPE_MAX: 'addNew.validation.subTypeMaxLen',
        END_BEFORE_START: 'addNew.validation.endBeforeStart',
        RELEASE_REQ: 'addNew.validation.releaseRequired',
        RELEASE_NAME_MAX: 'addNew.validation.releaseNameMaxLen',
        SPRINT_REQ: 'addNew.validation.sprintRequired',
        SPRINT_NAME_MAX: 'addNew.validation.sprintNameMaxLen',
        MILESTONE_REQ: 'addNew.validation.milestoneRequired',
        MILESTONE_NAME_MAX: 'addNew.validation.milestoneNameMaxLen',
      };
      const translatedMsg = validationResult.message && msgMap[validationResult.message]
        ? t(msgMap[validationResult.message])
        : (validationResult.message || t('addNew.validation.fallback'));
      toast.showError(t('addNew.validation.title'), translatedMsg);
      return;
    }

    
    try {
      if (selectedType === 'Phase') {
        const isAgileProject = formData.methodType === 'AGILE';
        
        const phaseData: PhaseDto = {
          type: formData.type as any,
          lifecycle: PhaseLifecycle.ACTIVE,
          startTime: formData.startTime,
          endTime: formData.endTime,
          subTypeAgile: isAgileProject ? formData.subTypeAgile || undefined : undefined,
          description: formData.phaseDescription.trim() || undefined,
          project: projectId,
          progress: 0
        };

        const createdPhase = await createPhaseMutation.mutateAsync({ data: phaseData });
        toast.showSuccess(t('addNew.toast.successTitle'), t('addNew.toast.phaseCreated', { name: formData.type }));
        
        if (onPhaseCreated) {
          onPhaseCreated(createdPhase);
        }
      } else if (selectedType === 'Milestones') {
        const milestoneData: MilestoneDto = {
          type: formData.milestoneType as MilestoneType,
          name: formData.name,
          deadline: formData.deadline,
          releaseRelevant: formData.releaseRelevant,
          description: formData.milestoneDescription.trim() || undefined,
          projectId: Number(projectId)
        };

        await createMilestoneMutation.mutateAsync({ data: milestoneData as any });
        toast.showSuccess(t('addNew.toast.successTitle'), t('addNew.toast.milestoneCreated', { name: formData.name }));
        
        if (onMilestoneCreated) {
          onMilestoneCreated();
        }
      } else if (selectedType === 'Releases') {
        const releaseData = {
          name: formData.name,
          startDate: formData.startDate,
          endDate: formData.endDate,
          releaseDate: formData.releaseDate,
          codeFreeze: formData.releaseCodeFreeze,
          projectId: formData.projectId
        };

        await createRelease(releaseData);
        toast.showSuccess(t('addNew.toast.successTitle'), t('addNew.toast.releaseCreated', { name: formData.name }));
        
        if (onReleaseCreated) {
          onReleaseCreated();
        }
      } else if (selectedType === 'Sprints') {
        const sprintData: any = {
          name: formData.name,
          startDate: formData.startDate,
          endDate: formData.endDate,
          codeFreeze: formData.sprintCodeFreeze,
          phase: Number(formData.phase)
        };
        
        /* Temporarily disabled , will be discussed in weekly
        if (formData.isRelatedToRelease && formData.releaseId) {
          // Force the releaseId to be included in the API call
          // The model might not include this field yet, but we can force it as any
          sprintData.releaseId = Number(formData.releaseId);
          console.log('Adding releaseId to sprint:', Number(formData.releaseId));
        }
        */
        await createSprint(sprintData);
        toast.showSuccess(t('addNew.toast.successTitle'), t('addNew.toast.sprintCreated', { name: formData.name }));
        
        if (onSprintCreated) {
          onSprintCreated();
        }
      }
      
      resetForm();
      onClose();
    } catch (error: any) {
      console.error(`Error creating ${selectedType}:`, error);
      toast.showError(t('addNew.toast.errorTitle'), getApiErrorMessage(error));
    }
  };

  const resetForm = () => {
    setFormData({
      // Common fields
      name: '',
      
      // Phase form fields
      type: '',
      startTime: '',
      endTime: '',
      subTypeAgile: '',
      
      // Release fields
      startDate: '',
      endDate: '',
      releaseDate: '',
      releaseCodeFreeze: '',
      
      // Sprint fields
      sprintCodeFreeze: false,
      phase: '',
      phases: phases,
      isRelatedToRelease: false,
      releaseId: '',
      releases: releases,
      
      phaseDescription: '',

      
      // Milestone fields
      milestoneType: '',
      milestoneDescription: '',
      deadline: '',
      releaseRelevant: true,
      
      projectId: projectId === undefined ? '' : String(projectId),
      projectType: projectType || '',
      methodType: methodType || ''
    });
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const renderForm = () => {
    switch (selectedType) {
      case 'Phase':
        return <PhaseForm formData={formData} onInputChange={handleInputChange} />;
      case 'Releases':
        return <ReleasesForm formData={formData} onInputChange={handleInputChange} />;
      case 'Sprints':
        return <SprintsForm formData={formData} onInputChange={handleInputChange} />;
      case 'Milestones':
        return <MilestonesForm
          formData={formData}
          onInputChange={handleInputChange}
        />;
      default:
        return <PhaseForm formData={formData} onInputChange={handleInputChange} />;
    }
  };

  return (
      <div className={styles.overlay} onClick={onClose}>
          <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
              <div className={styles.popupHeader}>
                  <div className={styles.headerContent}>
                      <h2 className={styles.headerTitle}>{t(`addNew.titles.${selectedType}`)}</h2>
                      <p className={styles.headerDescription}>
                          {t(`addNew.descriptions.${selectedType}`)}
                      </p>
                  </div>
              </div>

              <div className={styles.formContent}>
                  <div className={styles.formBody}>
                      <div className={styles.inputGroup}>
                          <label className={`${styles.inputLabel} ${styles.required}`}>{t('addNew.selectType')}</label>
                          <div className={styles.selectField}>
                              <select
                                  ref={firstInputRef}
                                  className={styles.select}
                                  value={selectedType}
                                  onChange={(e) => setSelectedType(e.target.value)}
                              >
                                  {formTypes.map(type => (
                                      <option key={type} value={type}>{t(`addNew.typeLabels.${type}`)}</option>
                                  ))}
                              </select>
                              <IconChevronDown/>
                          </div>
                      </div>
                      {renderForm()}
                  </div>
              </div>

              <div className={styles.popupFooter}>
                  <div className={styles.footerActions}>
                      <button className={styles.cancelButton} onClick={handleCancel}>
                          {t('addNew.cancel')}
                      </button>
                      <button
                          className={styles.submitButton}
                          onClick={handleSubmit}
                          disabled={!formConfigs[selectedType].validation(formData).isValid}
                      >
                          <IconPlus/>
                          {t(`addNew.submitTexts.${selectedType}`)}
                      </button>
                  </div>
              </div>
          </div>
      </div>
  );
};

export default AddNewItemPopup;